import { Fragment, Suspense, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import { Eye, FlaskConical, LifeBuoy, Menu, PanelLeftClose, PanelLeftOpen, Search, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api.ts";
import { useSession } from "@/lib/session.tsx";
import { useBrand } from "@/lib/brand.tsx";
import { cn, titleCase } from "@/lib/utils.ts";
import { useLocalStorage } from "@/hooks/use-local-storage.ts";
import { useIsMobile } from "@/hooks/use-media-query.ts";
import { modKeyLabel } from "@/hooks/use-hotkey.ts";
import { Button } from "@/components/ui/button.tsx";
import { Kbd } from "@/components/ui/kbd.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet.tsx";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb.tsx";
import { BrandLogo } from "./brand-logo.tsx";
import { CommandPalette, useCommandPalette } from "./command-palette.tsx";
import { ConfirmProvider } from "./confirm-dialog.tsx";
import { PageLoading } from "./loading.tsx";
import { navFor, flattenNav, type NavGroup, type NavItem } from "./nav.ts";
import { NotificationsPopover, useUnreadNotifications } from "./notifications-popover.tsx";
import { ThemeToggle } from "./theme-toggle.tsx";
import { UserMenu } from "./user-menu.tsx";

const SIDEBAR_KEY = "ui.sidebar.collapsed";

function NavLink({ item, collapsed, badge, onNavigate }: { item: NavItem; collapsed: boolean; badge?: number; onNavigate?: () => void }) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      activeOptions={{ exact: item.exact ?? false }}
      onClick={onNavigate}
      aria-label={collapsed ? item.label : undefined}
      className={cn(
        "group/nav relative flex h-9 items-center gap-2.5 rounded-md px-2.5 text-[13.5px] font-medium text-sidebar-foreground/85 transition-[background-color,color] duration-150 outline-none",
        "hover:bg-sidebar-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/40",
        "data-[status=active]:bg-sidebar-accent data-[status=active]:text-foreground data-[status=active]:before:absolute data-[status=active]:before:top-2 data-[status=active]:before:bottom-2 data-[status=active]:before:-left-2 data-[status=active]:before:w-0.5 data-[status=active]:before:rounded-full data-[status=active]:before:bg-primary",
        collapsed && "justify-center px-0",
      )}
    >
      <Icon className="size-4 shrink-0 text-sidebar-foreground/70 transition-colors group-hover/nav:text-foreground group-data-[status=active]/nav:text-primary" aria-hidden="true" />
      {!collapsed && <span className="truncate">{item.label}</span>}
      {badge !== undefined && badge > 0 && (
        <span className={cn("grid min-w-[18px] place-items-center rounded-full bg-primary px-1 text-[10px] leading-[18px] font-semibold text-primary-foreground tabular-nums", collapsed ? "absolute top-1 right-1 min-w-4 leading-4" : "ml-auto")} aria-label={`${badge} unread`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </Link>
  );
  if (!collapsed) return link;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={8}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

function SidebarNav({ groups, collapsed, badges, onNavigate }: { groups: NavGroup[]; collapsed: boolean; badges: Record<string, number>; onNavigate?: () => void }) {
  return (
    <nav aria-label="Main navigation" className={cn("flex flex-col gap-4 py-3", collapsed ? "px-2" : "px-3")}>
      {groups.map((g, gi) => (
        <div key={g.label} className="flex flex-col gap-0.5">
          {collapsed ? gi > 0 && <div className="mx-2 mb-1 h-px bg-sidebar-border" role="presentation" /> : <div className="mb-1 px-2.5 text-[11px] font-semibold tracking-wider text-sidebar-foreground/50 uppercase">{g.label}</div>}
          {g.items.map((item) => (
            <NavLink key={item.to} item={item} collapsed={collapsed} badge={item.badgeKey ? badges[item.badgeKey] : undefined} onNavigate={onNavigate} />
          ))}
        </div>
      ))}
    </nav>
  );
}

function Sidebar({ groups, collapsed, onToggle, badges }: { groups: NavGroup[]; collapsed: boolean; onToggle: () => void; badges: Record<string, number> }) {
  const { surface } = useSession();
  return (
    <aside data-collapsed={collapsed} className={cn("sticky top-0 hidden h-svh shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ease-out lg:flex", collapsed ? "w-14" : "w-60")}>
      <div className={cn("flex h-14 items-center border-b border-sidebar-border", collapsed ? "justify-center px-2" : "px-4")}>
        <Link to="/" className="flex min-w-0 items-center rounded-md focus-visible:ring-[3px] focus-visible:ring-ring/40 focus-visible:outline-none" aria-label="Home">
          <BrandLogo size="sm" wordmark={!collapsed} />
        </Link>
      </div>
      <ScrollArea className="min-h-0 flex-1" viewportClassName="scrollbar-thin">
        <SidebarNav groups={groups} collapsed={collapsed} badges={badges} />
      </ScrollArea>
      <div className={cn("flex items-center border-t border-sidebar-border p-2", collapsed ? "justify-center" : "justify-between")}>
        {!collapsed && <span className="px-2 text-[11px] font-medium tracking-wider text-sidebar-foreground/50 uppercase">{surface === "portal" ? "Customer portal" : surface === "platform" ? "Platform" : "Operator admin"}</span>}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon-sm" onClick={onToggle} aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} aria-pressed={collapsed} className="text-sidebar-foreground/70">
              {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? "Expand" : "Collapse"}</TooltipContent>
        </Tooltip>
      </div>
    </aside>
  );
}

interface Crumb {
  label: string;
  to?: string;
}

function useCrumbs(groups: NavGroup[]): Crumb[] {
  const { pathname } = useLocation();
  return useMemo(() => {
    const items = flattenNav(groups);
    const segs = pathname.split("/").filter(Boolean);
    const crumbs: Crumb[] = [];
    let acc = "";
    for (let i = 0; i < segs.length; i++) {
      const seg = segs[i] ?? "";
      acc += `/${seg}`;
      const nav = items.find((it) => it.to === acc);
      const last = i === segs.length - 1;
      const label = nav?.label ?? (/^\d+$/.test(seg) ? `#${seg}` : seg === "new" ? "New" : titleCase(decodeURIComponent(seg)));
      if (seg === "platform" && !nav) {
        crumbs.push({ label: "Platform", to: last ? undefined : "/platform" });
        continue;
      }
      crumbs.push({ label, to: last ? undefined : acc });
    }
    if (crumbs.length === 0) crumbs.push({ label: items.find((it) => it.to === "/")?.label ?? "Home" });
    return crumbs;
  }, [groups, pathname]);
}

function TopBar({ crumbs, onMenu }: { crumbs: Crumb[]; onMenu: () => void }) {
  const { operator, surface } = useSession();
  const brand = useBrand(operator);
  const openPalette = useCommandPalette((s) => s.setOpen);
  const help = surface === "portal" ? { to: "/help" } : brand.supportEmail ? { href: `mailto:${brand.supportEmail}` } : null;
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b bg-background/85 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:px-4 lg:px-6">
      <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenu} aria-label="Open navigation">
        <Menu />
      </Button>
      <Link to="/" className="mr-1 flex items-center lg:hidden" aria-label="Home">
        <BrandLogo size="sm" wordmark={false} />
      </Link>
      <Breadcrumb className="min-w-0 flex-1">
        <BreadcrumbList className="flex-nowrap">
          {crumbs.map((c, i) => {
            const last = i === crumbs.length - 1;
            return (
              <Fragment key={`${c.label}-${i}`}>
                <BreadcrumbItem className={cn(!last && "hidden sm:inline-flex")}>
                  {last || !c.to ? (
                    <BreadcrumbPage className="truncate">{c.label}</BreadcrumbPage>
                  ) : (
                    <BreadcrumbLink asChild>
                      <Link to={c.to} className="truncate">
                        {c.label}
                      </Link>
                    </BreadcrumbLink>
                  )}
                </BreadcrumbItem>
                {!last && <BreadcrumbSeparator className="hidden sm:block" />}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="hidden h-8 w-56 justify-start gap-2 pr-1.5 font-normal text-muted-foreground md:inline-flex" onClick={() => openPalette(true)} aria-label="Search" aria-keyshortcuts="Control+K Meta+K">
          <Search className="size-3.5" />
          <span className="flex-1 text-left">Search…</span>
          <Kbd>{modKeyLabel} K</Kbd>
        </Button>
        <Button variant="ghost" size="icon" className="md:hidden" onClick={() => openPalette(true)} aria-label="Search">
          <Search />
        </Button>
        <NotificationsPopover />
        <ThemeToggle />
        {help && (
          <Tooltip>
            <TooltipTrigger asChild>
              {"to" in help ? (
                <Button variant="ghost" size="icon" asChild>
                  <Link to={help.to} aria-label="Help">
                    <LifeBuoy />
                  </Link>
                </Button>
              ) : (
                <Button variant="ghost" size="icon" asChild>
                  <a href={help.href} aria-label="Contact support">
                    <LifeBuoy />
                  </a>
                </Button>
              )}
            </TooltipTrigger>
            <TooltipContent>Help</TooltipContent>
          </Tooltip>
        )}
        <UserMenu />
      </div>
    </header>
  );
}

function AssumeBanner() {
  const { me, refresh } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!me?.assume) return null;
  const exit = async () => {
    setBusy(true);
    try {
      await api.post("/api/auth/assume/exit");
      await refresh();
      void navigate({ to: "/" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not exit customer view");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div role="status" className="flex items-center gap-3 border-b border-amber-500/40 bg-amber-400/20 px-4 py-2 text-sm text-amber-950 dark:bg-amber-500/15 dark:text-amber-100">
      <Eye className="size-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1 truncate">
        Viewing as <strong className="font-semibold">{me.assume.label}</strong>
        <span className="hidden sm:inline"> — you're seeing exactly what this customer sees.</span>
      </p>
      <Button size="sm" variant="outline" className="h-7 border-amber-600/40 bg-background/60 hover:bg-background" loading={busy} onClick={() => void exit()}>
        <X className="size-3.5" /> Exit
      </Button>
    </div>
  );
}

function TestModeBanner() {
  const { hasFeature } = useSession();
  if (!hasFeature("test_mode_banner")) return null;
  return (
    <div role="status" className="flex items-center gap-2 border-b border-info/30 bg-info/10 px-4 py-1.5 text-xs text-foreground">
      <FlaskConical className="size-3.5 shrink-0 text-info" aria-hidden="true" />
      <span>
        <strong className="font-semibold">Test mode</strong> — documents are processed but not sent to live trading partners.
      </span>
    </div>
  );
}

export function AppShell() {
  const session = useSession();
  const brand = useBrand(session.operator);
  const [collapsed, setCollapsed] = useLocalStorage(SIDEBAR_KEY, false);
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const groups = useMemo(() => navFor(session), [session]);
  const { unread } = useUnreadNotifications();
  const badges = useMemo(() => ({ notifications: unread }), [unread]);
  const crumbs = useCrumbs(groups);

  useEffect(() => setMobileOpen(false), [pathname]);
  useEffect(() => {
    const page = crumbs[crumbs.length - 1]?.label;
    document.title = page ? `${page} · ${brand.title ?? brand.name}` : (brand.title ?? brand.name);
  }, [crumbs, brand.title, brand.name]);

  return (
    <TooltipProvider>
      <ConfirmProvider>
        <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-md focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:text-primary-foreground">
          Skip to content
        </a>
        <div className="flex min-h-svh bg-background">
          <Sidebar groups={groups} collapsed={collapsed} onToggle={() => setCollapsed((v) => !v)} badges={badges} />
          <div className="flex min-w-0 flex-1 flex-col">
            <AssumeBanner />
            <TestModeBanner />
            <TopBar crumbs={crumbs} onMenu={() => setMobileOpen(true)} />
            <main id="main" tabIndex={-1} className="flex-1 outline-none">
              <div className="page-grid">
                <Suspense fallback={<PageLoading />}>
                  <Outlet />
                </Suspense>
              </div>
            </main>
          </div>
        </div>
        {isMobile && (
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetContent side="left" className="w-72 gap-0 bg-sidebar p-0 text-sidebar-foreground">
              <SheetHeader className="h-14 flex-row items-center border-b border-sidebar-border px-4 py-0">
                <SheetTitle className="flex items-center">
                  <BrandLogo size="sm" />
                </SheetTitle>
                <SheetDescription className="sr-only">Main navigation</SheetDescription>
              </SheetHeader>
              <ScrollArea className="min-h-0 flex-1">
                <SidebarNav groups={groups} collapsed={false} badges={badges} onNavigate={() => setMobileOpen(false)} />
              </ScrollArea>
            </SheetContent>
          </Sheet>
        )}
        <CommandPalette />
      </ConfirmProvider>
    </TooltipProvider>
  );
}

export default AppShell;
